// src/components/HomePage.js
import React from "react";
import {
  FaShieldAlt,
  FaFingerprint,
  FaQrcode,
  FaBolt,
  FaApple,
  FaGooglePlay,
  FaChevronRight,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import SEO from "./SEO";
import Navbar from "./NavBar";
import LatestNews from "./LatestNews";
import FAQ from "./FAQ";
import "../App.css";
import GoodFirmsBadge from "../assets/partners/GoodFirms_Partner_1.svg";

export default function HomePage() {
  return (
    <div className="App">
      {/* ───── HEAD (SEO) ───── */}
      <SEO
        title="HashSeal | Seal & Verify Documents with AI & Blockchain"
        description="Stop document fraud at the source. HashSeal fingerprints issuers, seals on blockchain, and verifies via QR — for invoices, diplomas, ESG attestations and more."
        image="/og.png"
        canonical="/"
      />

      {/* ───── NAVBAR ───── */}
      <Navbar />

      {/* ───── HERO ───── */}
      <section className="hero-section" aria-label="Hero">
        <video
          className="hero-bg-video"
          src="/video-hashseal.mp4"
          poster="/video-hashseal-thumbnail.jpg"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
        <div className="hero-overlay" />
        <div className="container hero-content">
          <div className="hero-left">
            <h1>
              Seal &amp; Verify Documents
              <br /> with Blockchain &amp; AI
            </h1>

            <p className="hero-desc">
              Prevent forgeries and tampering. HashSeal gives every document a
              verifiable identity with AI fingerprinting, cryptographic hashing,
              and a QR you can trust.
            </p>

            <div className="hero-actions hero-actions-wrap">
              <Link to="/seal" className="btn btn-primary" aria-label="Seal or certify a document">
                <FaShieldAlt style={{ marginRight: 8 }} />
                Certify a Document
              </Link>

              <Link to="/verify" className="btn btn-verify" aria-label="Verify a document">
                <FaQrcode style={{ marginRight: 8 }} />
                Verify a Document
              </Link>

              {/* a11y-safe placeholder for coming-soon apps */}
              <button
                type="button"
                className="btn btn-outline"
                aria-disabled="true"
                title="Mobile apps coming soon"
              >
                <FaApple style={{ marginRight: 8 }} /> Get the App
              </button>
            </div>

            {/* Quick vertical links (now readable over video) */}
            <nav className="quick-links on-hero" aria-label="Popular links">
              <Link className="chip" to="/insurance">Insurance</Link>
              <Link className="chip" to="/education">Education</Link>
              <Link className="chip" to="/esg">ESG</Link>
              <Link className="chip" to="/sports">Sports Events</Link>
              <Link className="chip" to="/museum">Arts and Heritage</Link>
              <Link className="chip" to="/case-studies">Case Studies</Link>
            </nav>
          </div>
        </div>
      </section>

      {/* ───── FEATURES GRID ───── */}
      <section className="section features-section" id="features" aria-label="Key features">
        <div className="container">
          <h2 className="section-title">Key Features</h2>

          <div className="features-grid">
            <div className="feature-card">
              <FaFingerprint className="feature-icon" aria-hidden="true" />
              <h3>AI Fingerprinting</h3>
              <p>Issuer-specific AI detects subtle edits and advanced forgeries.</p>
            </div>

            <div className="feature-card">
              <FaShieldAlt className="feature-icon" aria-hidden="true" />
              <h3>Blockchain Sealing</h3>
              <p>Cryptographic hash + on-chain timestamp for immutable proof.</p>
            </div>

            <div className="feature-card">
              <FaQrcode className="feature-icon" aria-hidden="true" />
              <h3>QR Verification</h3>
              <p>Scan to verify instantly. Public checks or deeper accredited views.</p>
            </div>

            <div className="feature-card">
              <FaBolt className="feature-icon" aria-hidden="true" />
              <h3>Real-Time API</h3>
              <p>Drop-in webhooks and SDKs for automated triage and audits.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="section how-section" id="how" aria-label="How HashSeal works">
        <div className="container how-flex">
          <div className="how-steps">
            <h2 className="section-title">How HashSeal Works</h2>
            <ol>
              <li><strong>Upload</strong> your document or capture securely.</li>
              <li>
                <strong>AI fingerprints</strong> the issuer, hashes the file, and{" "}
                <strong>seals it on blockchain</strong>.
              </li>
              <li><strong>QR code is embedded</strong> in the document.</li>
              <li>
                Recipients <strong>scan or upload</strong> to verify — with tiered access
                for public, registered, and accredited verifiers.
              </li>
            </ol>

            <div className="how-ctas">
              <Link to="/seal" className="btn btn-primary">
                Certify Now <FaChevronRight style={{ marginLeft: 6 }} />
              </Link>
              <Link to="/verify" className="btn btn-verify">
                Verify Now <FaChevronRight style={{ marginLeft: 6 }} />
              </Link>
            </div>
          </div>

          <div className="how-image" aria-hidden="true">
            <video
              src="/HashSeal_v0.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="how-video"
              preload="metadata"
              style={{ maxWidth: "400px", borderRadius: "16px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}
            />
          </div>
        </div>
      </section>

      {/* ───── CERTIFY BANNER ───── */}
      <section className="certify-banner" aria-label="Call to action">
        <div className="container certify-flex">
          <div>
            <h2>Certify Your Documents Now</h2>
            <p>Seal invoices, receipts, and certificates with blockchain and AI. One click—unbreakable trust.</p>
          </div>
          <Link to="/seal" className="btn btn-glow">
            Go to Platform
          </Link>
        </div>
      </section>

      {/* ───── LATEST NEWS (stacked) ───── */}
      <section className="section" aria-label="Latest news">
        <div className="container">
          <LatestNews />
        </div>
      </section>

      {/* ───── FAQ (single title) ───── */}
      <section className="section" id="faq" aria-label="FAQ">
        <div className="container">
          <h2 className="section-title">FAQ</h2>
          <FAQ showTitle={false} />
        </div>
      </section>

      {/* ───── DOWNLOAD (placeholder CTA) ───── */}
      <section className="section download-section" id="download" aria-label="Mobile apps">
        <div className="container download-flex">
          <div>
            <h2>Get the HashSeal App</h2>
            <p>Verify, scan, and certify documents anytime, anywhere.</p>
            <div className="download-buttons">
              {/* placeholders to avoid invalid href warnings */}
              <button type="button" className="btn store-btn ios" aria-disabled="true" title="Coming soon">
                <FaApple /> App Store
              </button>
              <button type="button" className="btn store-btn android" aria-disabled="true" title="Coming soon">
                <FaGooglePlay /> Google Play
              </button>
            </div>
            <small>Coming soon for iOS &amp; Android</small>
          </div>
        </div>
      </section>

      {/* ───── PARTNERS ───── */}
      <section className="section partner-section" aria-label="Partners">
        <div className="container">
          <h2 className="section-title">Our Partners</h2>
          <div className="partner-logos">
            <a
              href="https://www.goodfirms.co"
              target="_blank"
              rel="sponsored noopener noreferrer"
              aria-label="GoodFirms"
              title="GoodFirms"
            >
              <img
                src={GoodFirmsBadge}
                alt="GoodFirms Partner Badge"
                className="partner-badge"
                width={160}
                height={48}
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer className="footer" role="contentinfo">
        <div className="container footer-flex">
          <div className="footer-links">
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <Link to="/onboarding">Onboarding</Link>
            <Link to="/case-studies">Case Studies</Link>
            <a href="#faq">FAQ</a>
            <Link to="/dashboard">Dashboard</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/cookie">Cookies</Link>
          </div>
          <div className="footer-brand">
            <span>
              {new Date().getFullYear()}{" "}
              <a
                href="https://capital-ai.eu/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                Capital AI
              </a>{" "}
              — HashSeal
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
